/* eslint-disable react-refresh/only-export-components */
import { DimeAspectRatio, DimeSeparator } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/aspect-ratio')({
  component: AspectRatioDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

const RATIOS = [
  {
    label: '16:9',
    ratio: 16 / 9,
    description: 'Video-like — embeds, hero media, thumbnails.',
    src: 'https://placehold.co/640x360',
    alt: 'Placeholder 16 by 9 video thumbnail',
    maxWidthClassName: 'max-w-sm',
  },
  {
    label: '1:1',
    ratio: 1 / 1,
    description: 'Square — avatars, product tiles, grid cells.',
    src: 'https://placehold.co/400x400',
    alt: 'Placeholder square tile',
    maxWidthClassName: 'max-w-48',
  },
  {
    label: '4:3',
    ratio: 4 / 3,
    description: 'Classic photo — legacy media, printed docs.',
    src: 'https://placehold.co/400x300',
    alt: 'Placeholder 4 by 3 layout',
    maxWidthClassName: 'max-w-xs',
  },
] as const;

const PROPS = [
  {
    prop: 'ratio',
    values: 'number (e.g. 16 / 9)',
    description: 'Width-to-height ratio applied via a padding-bottom layout trick. Defaults to 1.',
  },
  {
    prop: 'className',
    values: 'string',
    description:
      'Merged onto the inner content element — style media to fill the box (e.g. object-cover).',
  },
] as const;

function AspectRatioDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Aspect Ratio</h1>
      <p className="mb-8 text-muted-foreground">
        Constrains child content — images, video, embeds — to a fixed aspect ratio responsively,
        without layout-shift hacks. Pure layout primitive: no image-loading or skeleton behavior
        baked in.
      </p>

      <DocsSection title="Ratios">
        <div className="rounded-lg border border-border bg-background p-6">
          <div className="flex flex-col gap-6">
            {RATIOS.map(({ label, ratio, src, alt, maxWidthClassName }) => (
              <div key={label}>
                <p className="mb-2 font-mono text-xs text-muted-foreground">{label}</p>
                <div className={`w-full ${maxWidthClassName}`}>
                  <DimeAspectRatio ratio={ratio} className="overflow-hidden rounded-md bg-muted">
                    <img src={src} alt={alt} className="size-full object-cover" />
                  </DimeAspectRatio>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeAspectRatio</code> fills its parent's width and scales
          height to match, so each box keeps its ratio as the container resizes.
        </p>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Ratio</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {RATIOS.map(({ label, description }) => (
              <tr key={label} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{label}</td>
                <td className="py-2 text-muted-foreground">{description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="Portrait">
        <div className="rounded-lg border border-border bg-background p-6">
          <div className="mx-auto w-full max-w-40">
            <DimeAspectRatio ratio={9 / 16} className="overflow-hidden rounded-md bg-muted">
              <img
                src="https://placehold.co/360x640"
                alt="Placeholder portrait 9 by 16 layout"
                className="size-full object-cover"
              />
            </DimeAspectRatio>
          </div>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">ratio={'{9 / 16}'}</code> for tall, portrait-oriented
          media — mobile screenshots, story-style thumbnails, or scanned documents.
        </p>
      </DocsSection>

      <DocsSection title="Usage">
        <pre className="overflow-x-auto rounded-md bg-muted p-4 text-sm">
          <code>{`import { DimeAspectRatio } from '@nudool/design-system';

<DimeAspectRatio ratio={16 / 9} className="overflow-hidden rounded-md bg-muted">
  <img src="/media/hero.jpg" alt="Product hero shot" className="size-full object-cover" />
</DimeAspectRatio>`}</code>
        </pre>
        <p className="mt-3 text-sm text-muted-foreground">
          Give the child element <code className="font-mono">size-full object-cover</code> (or
          equivalent) so it fills the constrained box.
        </p>
      </DocsSection>

      <DocsSection title="Props">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Prop</th>
              <th className="py-2 pr-4 font-medium">Values</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {PROPS.map(({ prop, values, description }) => (
              <tr key={prop} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{prop}</td>
                <td className="py-2 pr-4 font-mono text-xs">{values}</td>
                <td className="py-2 text-muted-foreground">{description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>
    </div>
  );
}
